/**
 * Grafik batang berkelompok: cocok untuk membandingkan jumlah pegawai per tingkat
 * bintang (kategori diskrit) antar beberapa periode/bulan.
 */
import { useMemo } from "react";
import PropTypes from "prop-types";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import Icon from "@mui/material/Icon";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const COLORS = ["#1e88e5", "#43a047", "#fb8c00"];

function PrestasiBintangGroupedChart({ title, description, footerLines, labels, series }) {
  const chartConfig = useMemo(() => {
    const datasets = (series || []).map((s, i) => {
      const color = COLORS[i % COLORS.length];
      return {
        label: s.label,
        data: s.data,
        backgroundColor: color,
        borderColor: color,
        borderWidth: 0,
        borderRadius: 4,
        borderSkipped: false,
        maxBarThickness: 28,
      };
    });

    return {
      data: {
        labels: labels || [],
        datasets,
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: true,
            position: "top",
            labels: {
              boxWidth: 12,
              padding: 16,
              font: { size: 12, family: "Inter", weight: "400" },
              color: "#747474",
            },
          },
          tooltip: {
            intersect: false,
            mode: "index",
          },
        },
        interaction: {
          intersect: false,
          mode: "index",
        },
        scales: {
          y: {
            beginAtZero: true,
            stacked: false,
            grid: {
              drawBorder: false,
              display: true,
              drawOnChartArea: true,
              drawTicks: false,
              borderDash: [5, 5],
              color: "#e5e5e5",
            },
            ticks: {
              precision: 0,
              stepSize: 1,
              padding: 10,
              font: { size: 13, weight: 400, family: "Inter" },
              color: "#747474",
            },
          },
          x: {
            stacked: false,
            grid: {
              drawBorder: false,
              display: false,
            },
            ticks: {
              color: "#747474",
              padding: 8,
              font: {
                size: 18,
                weight: 600,
                family: "Inter",
                lineHeight: 1.2,
              },
              maxRotation: 0,
              minRotation: 0,
            },
          },
        },
      },
    };
  }, [labels, series]);

  return (
    <Card sx={{ height: "100%" }}>
      <MDBox p={2}>
        <MDTypography variant="h6" textTransform="capitalize">
          {title}
        </MDTypography>
        {description ? (
          <MDTypography component="div" variant="button" color="text" fontWeight="light">
            {description}
          </MDTypography>
        ) : null}
      </MDBox>
      <MDBox pl={2} pb={2} pr={3}>
        <MDBox borderRadius="lg" height="17rem">
          <Bar data={chartConfig.data} options={chartConfig.options} redraw />
        </MDBox>
        <Divider sx={{ my: 1 }} />
        <MDBox display="flex" flexDirection="column" gap={0.5}>
          {(footerLines || []).map((line, idx) => (
            <MDBox key={idx} display="flex" alignItems="center" gap={0.5}>
              <MDTypography variant="button" color="text" lineHeight={1} sx={{ mt: 0.15 }}>
                <Icon fontSize="small" sx={{ verticalAlign: "middle" }}>
                  schedule
                </Icon>
              </MDTypography>
              <MDTypography variant="button" color="text" fontWeight="light" fontSize="0.75rem">
                {line}
              </MDTypography>
            </MDBox>
          ))}
        </MDBox>
      </MDBox>
    </Card>
  );
}

PrestasiBintangGroupedChart.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.node,
  footerLines: PropTypes.arrayOf(PropTypes.string),
  labels: PropTypes.arrayOf(PropTypes.string).isRequired,
  series: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      data: PropTypes.arrayOf(PropTypes.number).isRequired,
    })
  ).isRequired,
};

export default PrestasiBintangGroupedChart;
